/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, { useCallback, useEffect, useState } from 'react';
import appTheme from '../../common/theme/appTheme.json';
import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { User } from '../../../shared/entities/user';
import { GridActionsCellItem, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import CustomDataGrid from '../../common/data_grid/CustomDataGrid';
import ModalDialog from '../../common/dialogs/ModalDialog';
import WarningIcon from '@mui/icons-material/Warning';
import { Link, useRouteMatch } from 'react-router-dom';
import ErrorMessage from '../../common/layout/ErrorMessage';
import { StyledProvisioningPageHeading } from '../StyledProvisioningPageHeading';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import CreateIcon from '@mui/icons-material/Create';
import { TopAlignedChipCell } from '../../common/data_grid/ChipCell';
import { RenderTopAlignedFixedHeightCell } from '../../common/data_grid/TopAlignedCell';

import { ajaxRaw } from '../../../utils/StoreUtils';

interface UsersTableProps {
  // Required for displaying tables in unit tests
  disableVirtualization?: boolean;
}

// Helper function to make the machine-readable roles more human friendly
export const cleanupRoles = (params: GridRenderCellParams<any, string[], any, any>) => {
  params.value = params.value?.map(value => {
    return value.replace(/^ROLE_/g, '').replace(/_/g, ' ');
  });
};

const UsersTable = ({ disableVirtualization = false }: UsersTableProps) => {
  const { path } = useRouteMatch();
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [usersArray, setUsersArray] = useState<User[]>([]);
  const [error, setError] = useState<string>('');
  const [deleteModalTarget, setDeleteModalTarget] = useState<User | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const loadTable = () => {
    setLoadingUsers(true);
    ajaxRaw.get(`/provisioning/users`).subscribe({
      next: value => {
        setLoadingUsers(false);
        setUsersArray(value.response);
      },
      error: err => {
        setLoadingUsers(false);
        setError(err.message);
      },
    });
  };
  useEffect(loadTable, []);

  const COLLAPSED_ROW_HEIGHT_PX = 49;
  const columns: GridColDef[] = [
    {
      field: 'lastName',
      headerName: 'Last name',
      flex: 1,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'firstName',
      headerName: 'First name',
      flex: 1,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 2,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'orgName',
      headerName: 'Organization',
      flex: 1,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'roles',
      headerName: 'User role(s)',
      flex: 2,
      renderCell: params => {
        cleanupRoles(params);
        return <TopAlignedChipCell params={params} />;
      },
      sortable: false,
    },
    {
      field: 'rolesInheritedFromOrg',
      headerName: 'Organization role(s)',
      flex: 2,
      renderCell: params => {
        cleanupRoles(params);
        return <TopAlignedChipCell params={params} />;
      },
      sortable: false,
    },
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      flex: 1,
      align: 'right',
      cellClassName: 'ActionsCell-TopAligned',
      getActions: params => [
        <GridActionsCellItem
          key={'create-user'}
          icon={<CreateIcon />}
          component={Link}
          to={`${path}/edit/${params.row.id}`}
        />,
        <GridActionsCellItem
          key={'delete-user'}
          icon={<DeleteIcon />}
          onClick={() => {
            setDeleteModalTarget(params.row);
            setShowDeleteModal(true);
          }}
        />,
      ],
    },
  ];

  const AddUserButton = useCallback(
    () => (
      <Button
        component={Link}
        to={`${path}/new`}
        startIcon={<AddIcon />}
        variant="contained"
        sx={{
          marginLeft: 'auto',
          color: '#000000',
          letterSpacing: '0.8px',
          backgroundColor: appTheme.mptLightBlue,
          '&:hover': {
            backgroundColor: appTheme.mptLightBlue,
            svg: {
              color: '#000000',
            },
          },
        }}
      >
        Provision new user
      </Button>
    ),
    [path],
  );

  return (
    <>
      {error ? <ErrorMessage>{error}</ErrorMessage> : null}

      <StyledProvisioningPageHeading>Users {usersArray ? `(${usersArray.length})` : ''}</StyledProvisioningPageHeading>

      {loadingUsers ? <ModalLoadingSpinner /> : null}

      <Box display="flex" justifyContent="space-between" flexDirection={'column'} height={'100%'}>
        <CustomDataGrid
          rows={usersArray || []}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => COLLAPSED_ROW_HEIGHT_PX}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: 'lastName', sort: 'asc' }],
            },
          }}
          toolbarActionButtons={[<AddUserButton key={'add-user-button'} />]}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: 'Users',
              },
            },
          }}
          disableVirtualization={disableVirtualization}
        />
      </Box>

      <ModalDialog
        open={showDeleteModal}
        onLeftBtnClicked={() => {
          setShowDeleteModal(false);
        }}
        onRightBtnClicked={() => {
          ajaxRaw.delete(`/provisioning/users/${deleteModalTarget!.id}`).subscribe({
            next: value => {
              loadTable();
              setShowDeleteModal(false);
            },
            error: err => {
              loadTable();
              setError(err);
              setShowDeleteModal(false);
            },
          });
        }}
        onExited={() => {
          // Remove the delete target after the dialog's exit animation has finished. Otherwise, the `message` below
          // will briefly display `undefined` for the organization's display name
          setDeleteModalTarget(undefined);
        }}
        title={
          <Box display={'flex'} gap={'8px'} alignItems={'center'}>
            <WarningIcon htmlColor={'#FFB029'} /> Confirm User Deletion
          </Box>
        }
        message={
          <>
            Are you sure you want to delete the account for <code>{deleteModalTarget?.email}</code>?
            <br />
            <br />
            This action cannot be undone.
          </>
        }
      />
    </>
  );
};

export default UsersTable;
